/* =======================================================================
   assets/price-fetch.js.liquid
   – Dynamic sweepstakes entries + variant-aware pricing
   ======================================================================= */

/* ---------- helpers --------------------------------------------------- */
function sanitizeId(str) {
  return String(str).replace(/\D/g, '');
}

/**
 * Get membership tier multiplier from body data attributes
 * Returns { enabled, tierMultiplier, tierName }
 */
function getMembershipMultiplier() {
  const body = document.body;
  const enabled = body.dataset.membershipMultiplierEnabled === 'true';
  const tierMultiplier = parseFloat(body.dataset.membershipMultiplier) || 1;
  const tierName = body.dataset.membershipTier || 'none';
  return { enabled, tierMultiplier, tierName };
}

/**
 * Get effective multiplier using "higher value wins" logic
 * @param {number} productMultiplier - The product's CleanSweeps multiplier
 * @param {number} tierMultiplier - The customer's membership tier multiplier
 * @returns {{ effectiveMultiplier: number, source: 'product' | 'tier' | 'none' }}
 */
function getEffectiveMultiplier(productMultiplier, tierMultiplier) {
  const prodMult = Number(productMultiplier) || 1;
  const tierMult = Number(tierMultiplier) || 1;

  if (tierMult > prodMult) {
    return { effectiveMultiplier: tierMult, source: 'tier' };
  } else if (prodMult > 1) {
    return { effectiveMultiplier: prodMult, source: 'product' };
  }
  return { effectiveMultiplier: 1, source: 'none' };
}

function formatSubtotal(cents) {
  const s = String(cents);
  return s.length > 2 ? parseFloat(`${s.slice(0, -2)}.${s.slice(-2)}`) : cents / 100;
}

function withCommas(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  return n.toLocaleString('en-US'); // e.g., 1002303 -> "1,002,303"
}

/* ---------- core GraphQL fetch & DOM update --------------------------- */
function runSweepstakesFetch(cardEl) {
  if (!cardEl?.isConnected) return; // node got removed; guard

  const productId    = `gid://shopify/Product/${cardEl.dataset.productId}`;
  const platformId   = `gid://shopify/Shop/${cardEl.dataset.shopGid}`;
  const subtotal     = parseFloat(cardEl.dataset.subtotal);
  const sweepstakeId = sanitizeId(cardEl.dataset.sweepstakeId) || 'default';

  const query = `
    query QuerySweepstakeDetailsByProductId(
      $platform: String!
      $platformId: String!
      $productIds: [String!]
    ) {
      sweepstakeDetailsByProductId(
        platform: $platform
        platformId: $platformId
        productIds: $productIds
      ) {
        id
        entryManagement
        productsAllowedByDefault
        bonusEntriesDefault
        isActive
        scheduledBonusValue
        scheduledBonusActive
        products(productIds: $productIds) {
          productId
          productAllowed
          productBonusEntries
          productMultiplier
        }
      }
    }
  `;

  const variables = {
    platform:   'Shopify',
    platformId: platformId,
    productIds: [productId],
  };

  (async () => {
    try {
      const res = await fetch('https://backend.cleansweeps.app/graphql', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ query, variables }),
      });
      if (!res.ok) throw new Error(`Network error: ${res.status}`);

      const json = await res.json();
      if (json.errors?.length) {
        console.error('GraphQL errors:', json.errors);
        return;
      }

      const details = json.data?.sweepstakeDetailsByProductId;
      if (!details?.length) return;

      const matched =
        details.find(d => sanitizeId(d.id) === sweepstakeId) ||
        details.find(d => d.isActive) ||
        details[0];
      if (!matched) return;

      /* ----- calculations -------------------------------------------- */
      const entryMgmt   = JSON.parse(matched.entryManagement);
      const dollarSpent = parseFloat(entryMgmt.dollarSpent);
      const baseEntriesPer  = parseFloat(entryMgmt.entryPer);
      const roundUp     = entryMgmt.roundUp ?? true;
      const roundFn     = roundUp ? Math.ceil : Math.floor;

      // Scheduled bonus override (e.g., 2X promo)
      const scheduledBonusActive = matched?.scheduledBonusActive === true;
      const scheduledBonusValueNum = Number(matched?.scheduledBonusValue);
      const entriesPer =
        scheduledBonusActive && Number.isFinite(scheduledBonusValueNum) && scheduledBonusValueNum > 0
          ? scheduledBonusValueNum
          : baseEntriesPer;

      // Get membership tier multiplier
      const membership = getMembershipMultiplier();

      let finalMultiplier = entriesPer;
      let finalEntries;
      let multiplierSource = 'none'; // Track where multiplier comes from

      if ((!matched.products?.length) && matched.productsAllowedByDefault) {
        // No product-specific data, check if tier multiplier should apply
        if (membership.enabled && membership.tierMultiplier > 1) {
          finalEntries =
            roundFn((entriesPer / dollarSpent) * roundFn(subtotal) * membership.tierMultiplier) +
            matched.bonusEntriesDefault;
          finalMultiplier = roundFn(entriesPer * membership.tierMultiplier);
          multiplierSource = 'tier';
        } else {
          finalEntries =
            roundFn((entriesPer / dollarSpent) * roundFn(subtotal)) +
            matched.bonusEntriesDefault;
        }
      } else {
        const prod = matched.products?.[0];
        if (!prod?.productAllowed) {
          finalEntries = 'Product Not Eligible';
        } else {
          const prodMult = parseFloat(prod.productMultiplier) || 1;

          // Apply "higher value wins" logic for membership vs product multiplier
          let effectiveMult = prodMult;
          if (membership.enabled && membership.tierMultiplier > 1) {
            const result = getEffectiveMultiplier(prodMult, membership.tierMultiplier);
            effectiveMult = result.effectiveMultiplier;
            multiplierSource = result.source;
          } else if (prodMult > 1) {
            multiplierSource = 'product';
          }

          finalEntries =
            roundFn((entriesPer / dollarSpent) * roundFn(subtotal) * effectiveMult) +
            prod.productBonusEntries;

          // IMPORTANT: this becomes your display multiplier
          finalMultiplier = roundFn(entriesPer * effectiveMult);
        }
      }

      /* ----- DOM updates --------------------------------------------- */

      const productIdRaw = String(cardEl.dataset.productId);

      // If the multiplier span DOES NOT exist in the DOM, the user has "toggled off" multiplier
      // (or you’re in a context where you didn't render it). In that case, never show multiplier.
      const multiplierBoxes = cardEl.querySelectorAll(
        `#flash-text-dynamic-${productIdRaw}`
      );
      const canShowMultiplier = multiplierBoxes.length > 0;

      const isEligible = finalEntries !== 'Product Not Eligible';

      // "Real multiplier" means: eligible AND > 1.
      const realMultiplier = Number(finalMultiplier);
      const hasRealMultiplier = isEligible && Number.isFinite(realMultiplier) && realMultiplier > 1;

      // Only show when both: (a) user rendered multiplier span (toggle ON), and (b) multiplier > 1
      const showMultiplier = canShowMultiplier && hasRealMultiplier;

      // Update multiplier chip(s)
      if (multiplierBoxes.length) {
        for (const multiplierBox of multiplierBoxes) {
          if (showMultiplier) {
            multiplierBox.classList.remove('hidden');
            multiplierBox.textContent = `${realMultiplier}X`;

            // Add tier-specific styling for membership multipliers
            multiplierBox.dataset.multiplierSource = multiplierSource;
            multiplierBox.classList.remove('multiplier--tier', 'multiplier--product');
            if (multiplierSource === 'tier') {
              multiplierBox.classList.add('multiplier--tier');
              // Add tier name for color styling
              multiplierBox.dataset.tierName = membership.tierName;
            } else {
              multiplierBox.classList.add('multiplier--product');
            }
          } else {
            multiplierBox.classList.add('hidden');
            multiplierBox.textContent = ''; // avoid stale X
            multiplierBox.classList.remove('multiplier--tier', 'multiplier--product');
            delete multiplierBox.dataset.multiplierSource;
            delete multiplierBox.dataset.tierName;
          }
        }
      }

      // Toggle layout classes on any entries container(s) that match this product+sweepstake
      // You use the same id pattern for both contexts: product-price AND collection/card-gallery.
      const entriesContainers = cardEl.querySelectorAll(
        `#entries-container-${productIdRaw}-${sweepstakeId}`
      );
      if (entriesContainers.length) {
        for (const c of entriesContainers) {
          // New canonical class: use this in CSS
          c.classList.toggle('has-real-multiplier', showMultiplier);

          // Back-compat with any existing CSS you still have using .has-multiplier
          c.classList.toggle('has-multiplier', showMultiplier);
        }
      }

      // Update entries text
      const entriesBox = cardEl.querySelector(
        `#entries-text-placeholder-thumb-${productIdRaw}-${sweepstakeId}`
      );
      if (!entriesBox) return;

      const isIneligible = finalEntries === 'Product Not Eligible';
      entriesBox.classList.toggle('cs-band-entries--ineligible', isIneligible);
      entriesBox.textContent = isIneligible
        ? finalEntries
        : `${withCommas(finalEntries)} Entries`;
    } catch (err) {
      console.error('Sweepstake fetch error:', err);
    }
  })();
}

/* ---------- variant-change awareness ---------------------------------- */
function setupVariantSync() {
  const productJsonEl = document.querySelector(
    '[type="application/json"][data-product]'
  );
  if (!productJsonEl) return;

  const productJson   = JSON.parse(productJsonEl.textContent) || {};
  const productIdAttr = String(productJson.id);

  const variantData = productJson.variants || [];
  if (!variantData.length) return;

  const priceByVariant = Object.fromEntries(
    variantData.map(v => [Number(v.id), parseInt(v.price, 10)])
  );

  const variantSelect = document.querySelector('select[name="id"]');
  if (!variantSelect) return;

  let prevSubtotal = null;

  const refresh = () => {
    const vid   = Number(variantSelect.value);
    const cents = priceByVariant[vid];
    if (!cents) {
      setTimeout(refresh, 60);
      return;
    }

    const subtotal = formatSubtotal(cents);
    if (subtotal === prevSubtotal) return;
    prevSubtotal = subtotal;

    // always rescan to catch newly injected nodes
    scan();

    document
      .querySelectorAll(
        `[data-price-context="card"][data-product-id="${productIdAttr}"]`
      )
      .forEach(card => {
        card.dataset.subtotal = subtotal;
        runSweepstakesFetch(card);
      });
  };

  variantSelect.addEventListener('change', refresh);
  window.addEventListener('popstate', refresh);
}

/* ---------- per-card init -------------------------------------------- */
function initOneCard(el) {
  if (el.__priceCardInit) return; // idempotent
  el.__priceCardInit = true;
  runSweepstakesFetch(el);
}

/* ---------- scanning utilities --------------------------------------- */
function scan(root = document) {
  root
    .querySelectorAll('[data-price-context="card"]')
    .forEach(initOneCard);
}

/* ---------- force recalculation (for preview mode) ------------------- */
/**
 * Force recalculation of all entries on the page.
 * Used by the dev preview panel when tier changes.
 */
function forceRecalculateAllEntries() {
  console.log('[price-fetch] Forcing recalculation of all entries...');

  // Clear the init flag on all cards so they can be recalculated
  document.querySelectorAll('[data-price-context="card"]').forEach(el => {
    delete el.__priceCardInit;
  });

  // Re-scan and recalculate all cards
  document.querySelectorAll('[data-price-context="card"]').forEach(el => {
    el.__priceCardInit = true;
    runSweepstakesFetch(el);
  });

  // Also recalculate cart entries if on cart page
  if (typeof calculateCartEntries === 'function') {
    calculateCartEntries();
  }
}

// Expose globally for the preview panel to call
window.pchForceRecalculateEntries = forceRecalculateAllEntries;

// Listen for tier change events from the preview panel
document.addEventListener('pch:tierChanged', () => {
  forceRecalculateAllEntries();
});

/* ---------- bootstrapping -------------------------------------------- */
document.addEventListener('DOMContentLoaded', () => {
  scan();
  setupVariantSync();
});
document.addEventListener('shopify:section:load', e => scan(e.target));

const observer = new MutationObserver(muts => {
  for (const m of muts) {
    m.addedNodes.forEach(node => {
      if (node.nodeType !== 1) return;
      if (node.matches?.('[data-price-context="card"]')) initOneCard(node);
      if (node.querySelector) scan(node);
    });
  }
});
observer.observe(document.documentElement, { childList: true, subtree: true });

/* ==========================================================================
   CART ENTRIES CALCULATION
   ========================================================================== */

/**
 * Calculate total entries for all items in cart
 */
async function calculateCartEntries() {
  const cartDisplay = document.getElementById('cart-entries-display');
  if (!cartDisplay) return;

  const shopGid = cartDisplay.dataset.shopGid;
  const memberMultiplier = parseFloat(cartDisplay.dataset.memberMultiplier) || 1;
  const itemCount = parseInt(cartDisplay.dataset.itemCount, 10) || 0;

  if (itemCount === 0) {
    updateCartEntriesDisplay(0);
    return;
  }

  // Collect cart items data
  const cartItems = [];
  for (let i = 1; i <= itemCount; i++) {
    const productId = cartDisplay.dataset[`item${i}ProductId`];
    const quantity = parseInt(cartDisplay.dataset[`item${i}Quantity`], 10) || 1;
    const price = parseFloat(cartDisplay.dataset[`item${i}Price`]) || 0;
    if (productId) {
      cartItems.push({ productId, quantity, price });
    }
  }

  if (cartItems.length === 0) {
    updateCartEntriesDisplay(0);
    return;
  }

  // Build product IDs for GraphQL query
  const productIds = cartItems.map(item => `gid://shopify/Product/${item.productId}`);
  const platformId = `gid://shopify/Shop/${shopGid}`;

  const query = `
    query QuerySweepstakeDetailsByProductId(
      $platform: String!
      $platformId: String!
      $productIds: [String!]
    ) {
      sweepstakeDetailsByProductId(
        platform: $platform
        platformId: $platformId
        productIds: $productIds
      ) {
        id
        entryManagement
        productsAllowedByDefault
        bonusEntriesDefault
        isActive
        scheduledBonusValue
        scheduledBonusActive
        products(productIds: $productIds) {
          productId
          productAllowed
          productBonusEntries
          productMultiplier
        }
      }
    }
  `;

  const variables = {
    platform: 'Shopify',
    platformId: platformId,
    productIds: productIds,
  };

  try {
    const res = await fetch('https://backend.cleansweeps.app/graphql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, variables }),
    });

    if (!res.ok) throw new Error(`Network error: ${res.status}`);

    const json = await res.json();
    if (json.errors?.length) {
      console.error('GraphQL errors:', json.errors);
      return;
    }

    const details = json.data?.sweepstakeDetailsByProductId;
    if (!details?.length) return;

    // Find active sweepstake
    const matched = details.find(d => d.isActive) || details[0];
    if (!matched) return;

    const entryMgmt = JSON.parse(matched.entryManagement);
    const dollarSpent = parseFloat(entryMgmt.dollarSpent);
    const baseEntriesPer = parseFloat(entryMgmt.entryPer);
    const roundUp = entryMgmt.roundUp ?? true;
    const roundFn = roundUp ? Math.ceil : Math.floor;

    // Scheduled bonus override (e.g., 2X promo)
    const scheduledBonusActive = matched?.scheduledBonusActive === true;
    const scheduledBonusValueNum = Number(matched?.scheduledBonusValue);
    const entriesPer =
      scheduledBonusActive && Number.isFinite(scheduledBonusValueNum) && scheduledBonusValueNum > 0
        ? scheduledBonusValueNum
        : baseEntriesPer;

    // Calculate entries for each cart item
    let totalEntries = 0;

    for (const item of cartItems) {
      const productGid = `gid://shopify/Product/${item.productId}`;
      const prod = matched.products?.find(p => p.productId === productGid);

      let itemEntries = 0;

      if (prod && prod.productAllowed) {
        const prodMult = parseFloat(prod.productMultiplier) || 1;
        // Apply "higher value wins" logic
        const effectiveMult = Math.max(prodMult, memberMultiplier);
        itemEntries = roundFn((entriesPer / dollarSpent) * roundFn(item.price) * effectiveMult) + (prod.productBonusEntries || 0);
      } else if (matched.productsAllowedByDefault) {
        // No product-specific data, use defaults with member multiplier
        const effectiveMult = memberMultiplier > 1 ? memberMultiplier : 1;
        itemEntries = roundFn((entriesPer / dollarSpent) * roundFn(item.price) * effectiveMult) + (matched.bonusEntriesDefault || 0);
      }

      totalEntries += itemEntries;
    }

    updateCartEntriesDisplay(totalEntries);

  } catch (err) {
    console.error('Cart entries fetch error:', err);
  }
}

/**
 * Update the cart entries display with calculated total
 */
function updateCartEntriesDisplay(totalEntries) {
  const entriesEl = document.getElementById('cart-total-entries');
  if (entriesEl) {
    entriesEl.textContent = withCommas(totalEntries);
  }

  const cartDisplay = document.getElementById('cart-entries-display');
  if (cartDisplay) {
    cartDisplay.classList.remove('cart-entries-display--loading');
  }
}

// Initialize cart entries on page load
document.addEventListener('DOMContentLoaded', () => {
  calculateCartEntries();
});

// Re-calculate when cart is updated (for AJAX carts)
document.addEventListener('cart:updated', () => {
  calculateCartEntries();
});
